import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  forwardRef,
  memo,
} from "react";
import { useJackpotPlates } from "../../../Games/Shared/jackpotPlates";
import { Container, Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../pixiSettings";
import {
  useSlotRegistry,
  useSlot,
} from "../../../Contexts/SlotRegistryContext";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import {
  selectFreeSpinsActive,
  selectFreeSpinsReloadPhase,
  selectShowGameAfterTransition,
  selectIntroPopupActive,
} from "../../../State/selectors";
import {
  setShowFreeSpinIntro,
  setShowGambleDialog,
  setCriticalAnimationPlaying,
  setUserInterfaceDisabled,
  setShowFreeSpinsReload,
  setShowFreeSpinTotalWin,
  setShowFreeSpinZeroWin,
  setShowFreeSpinMaxWin,
  setFeatureSpinActive,
} from "../../../State/actions";
import GameViewSimpleController, {
  NATIVE_REELSET_SIZE,
  resolveBoardPlacement,
} from "./GameViewSimpleController";
import { selectHoldAndWinActive } from "../../../State/Slices/holdAndWinSlice";
import LevelChargingAnimator from "../../../Engine/PotCharging/LevelChargingAnimator";
import ReelSet from "../../../Games/Main/ReelSet/ReelSet";
import OverlayReelSet from "../../../Games/Main/OverlayReelSet/OverlayReelSet";
import DimReelSet from "../../../Games/Shared/DimReelSet/DimReelSet";
import WinBoxContainer from "../../../Games/Shared/WinBoxReelSet/WinBoxContainer";
import WinLinesContainer from "../../../Games/Shared/WinLinesReelSet/WinLinesContainer";
import { WinTextSized } from "../WinText/WinText";
import { WIN_TEXT_CONFIG } from "../WinText/WinTextController";
import {
  attemptFullscreenToggle,
  isPhysicalDesktop,
  isInFullscreen,
} from "../../../utilities";
import store from "../../../State/Store/store";
import { fsDiag } from "../../../utils/fsDiag";
import TickerTimeout from "../../../utils/TickerTimeout";
import {
  selectFreeSpinOutroPopup,
  FS_OUTRO_POPUP,
} from "../../../Games/Shared/freeSpinOutroPopup";
import { Settings } from "../../../settings";

// Fine-tuning offsets for the injected containers, in spine-local pixels.
// Both containers stack on the same grid, so they must move together.
const REELSET_X_OFFSET = 0;
// const REELSET_Y_OFFSET = 0;
const REELSET_Y_OFFSET = -1; // 1 up, on the window's centre (2026-10-12)

// Background taps enter fullscreen. Rate-limited because a tap on a touch
// device can arrive as several events.
const MIN_BACKGROUND_CLICK_GAP = 100;

// How long a completion reload waits for the restored round's reveal to start
// before it opens the outro anyway: 50 polls at 100ms, so five seconds.
// Generous, because the reveal cannot start until the reels are built;
// bounded, because a round with no way out is worse than an early dialog.
const REVEAL_START_MAX_WAITS = 50;

// ============================================================================
// RESIZE WITHOUT RE-RENDERING THE SCENE
//
// Every resize is applied, unthrottled, and as cheaply as possible:
//
//   GameViewSimple (the shell)  reads the size and applies it imperatively in
//                               layout effects: the spine's placement, the
//                               no-spine backup placement, the golden trail.
//   BackgroundTapSurface        reads it for the full-screen tap sprite.
//
// The scene (GameViewScene: spine, reel set, dim, overlay, reload logic) is
// memoized on props that do not change per resize (compositeCellHeight moves
// only between density buckets), so a resize does not render it. The reels and the overlay are built in the spine's native units
// (NATIVE_REELSET_SIZE) and inherit the spine's scale; nothing is
// counter-scaled.
//
// useLayoutEffect, not useEffect: the spine is placed in the same commit as
// the new size, before PIXI draws the next frame.
// ============================================================================

/**
 * The overlay's children: overlay reel set, win texts, win boxes. Native
 * sizes, like the reels, so a resize never renders this.
 */
const GameViewOverlayContent = () => {
  const { symbolWidth, symbolHeight, reelSpacing } = NATIVE_REELSET_SIZE;
  const { globalLineWinTextRef, lineWinTextRef, winBoxContainerRef } =
    useReelRefs();
  return (
    <>
      <OverlayReelSet
        symbolWidth={symbolWidth}
        symbolHeight={symbolHeight}
        reelSpacing={reelSpacing}
      />

      {/* Win lines UNDER the win boxes (3) and the symbols (5) — 2026-10-13. */}
      {/* Moved to the reel container (GameViewScene): its window clip hides
          the oversized ends under the pillars. */}
      {/* {FEATURES_CONFIG.WIN_LINES.ENABLED && (
        <WinLinesContainer
          ref={winLinesContainerRef}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          zIndex={2}
        />
      )} */}

      {FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED && (
        <Container name="win_text_overlay" zIndex={6}>
          <WinTextSized
            ref={globalLineWinTextRef}
            fontSizeMultiplier={WIN_TEXT_CONFIG.GLOBAL_WIN_FONT_SIZE}
            skin={WIN_TEXT_CONFIG.GLOBAL_WIN_SKIN}
            hideOnBigWin={true}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
          />
          <WinTextSized
            ref={lineWinTextRef}
            fontSizeMultiplier={WIN_TEXT_CONFIG.LINE_WIN_FONT_SIZE}
            skin={WIN_TEXT_CONFIG.LINE_WIN_SKIN}
            hideOnBigWin={true}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
          />
        </Container>
      )}

      {FEATURES_CONFIG.WIN_BOX.ENABLED && (
        <WinBoxContainer
          ref={winBoxContainerRef}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          reelSpacing={reelSpacing}
        />
      )}
    </>
  );
};

/** Invisible full-screen tap surface for fullscreen — sized to the screen. */
const BackgroundTapSurface = () => {
  const dispatch = useDispatch();
  const { dimensions, device } = useDimensions();
  const lastBackgroundClickRef = useRef(0);
  const fullscreenSupported = useSelector(
    (state) => state.settings.fullscreenSupported,
  );

  // Physical device capability is stable for the session.
  const isActualDesktop = useMemo(() => isPhysicalDesktop(), []);

  // The click catcher only ever does something when this is true. Without the
  // check it would show a pointer cursor over the whole game on desktop, where
  // clicking it does nothing.
  const backgroundClickIsActionable =
    !isActualDesktop && fullscreenSupported && !isInFullscreen();

  // Background tap → fullscreen
  const handleBackgroundClick = useCallback(
    (event) => {
      const now = Date.now();
      if (now - lastBackgroundClickRef.current < MIN_BACKGROUND_CLICK_GAP) return;
      lastBackgroundClickRef.current = now;

      if (event?.stopPropagation) event.stopPropagation();

      if (
        !isPhysicalDesktop() &&
        !isInFullscreen() &&
        store.getState().settings.fullscreenSupported
      ) {
        attemptFullscreenToggle(dispatch, device);
      }
    },
    [dispatch, device],
  );

  return (
    <Sprite
      texture={PIXI.Texture.EMPTY}
      x={0}
      y={0}
      width={dimensions.width}
      height={dimensions.height}
      zIndex={0}
      eventMode={backgroundClickIsActionable ? "static" : "none"}
      cursor={backgroundClickIsActionable ? "pointer" : "default"}
      pointertap={handleBackgroundClick}
    />
  );
};

/**
 * GameViewScene: the base game scene.
 *
 * Renders the game_view spine (GameViewSimpleController) and injects two
 * containers into its slots: the reels (under the frame) and the overlay
 * (above it). Also owns the dim, the golden trail layer and reload recovery.
 *
 * Memoized, and no prop changes per resize (see RESIZE above). The refs
 * belong to the shell, so the shell can place them.
 */
const GameViewScene = memo(({
  sizeRef,
  controllerRef,
  containerRef,
  reelContainerRef,
  overlayContainerRef,
  reelSlot,
  overlaySlot,
  compositeCellHeight,
}) => {
  const dispatch = useDispatch();
  // The jackpot plates' values, on the current bet (Games/Shared/jackpotPlates).
  useJackpotPlates(controllerRef);
  const goldenTrailContainerRef = useRef(null);

  const slotRegistry = useSlotRegistry();
  const {
    dimReelSetRef,
    gameViewControllerRef,
    goldenTrailAnimatorRef,
    winLinesContainerRef,
  } = useReelRefs();

  // The base room steps aside while free spins or Hold & Win own the screen,
  // taking the board injected into its spine with it.
  //
  // Hidden by ALPHA (0.001), never by `visible`: PIXI skips transform and
  // render for an invisible subtree, so the spine, its slot objects and its
  // atlas go cold, and the frame it comes back on pays for all of it at once
  // (a hitch on the transition).
  const freeSpinsActive = useSelector(selectFreeSpinsActive);
  // Hold & Win takes the screen the same way (its own slice).
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  // ── Reload recovery ──
  // The provider derives the phase, the slice records it, and the scene puts
  // the matching screen up.
  const freeSpinsReloadPhase = useSelector(selectFreeSpinsReloadPhase);
  const showGameAfterTransition = useSelector(selectShowGameAfterTransition);
  // One-shot: a boot step must not re-run on a render and reopen a screen
  // already answered.
  const freeSpinsRecoveryDoneRef = useRef(false);

  // ── ...re-armed when the feature goes back to idle ──
  // Every refreshGameData re-arms reloadPopupPending. Without the re-arm no
  // screen would clear it, handleResume would refuse for ever, and recovery
  // would work only once per page load.
  useEffect(() => {
    if (!freeSpinsActive && freeSpinsReloadPhase === "none") {
      freeSpinsRecoveryDoneRef.current = false;
    }
  }, [freeSpinsActive, freeSpinsReloadPhase]);

  // One frozen object: same identity every render, so a resize re-renders
  // neither the reel set nor its dim.
  const reelSetSize = NATIVE_REELSET_SIZE;

  // ─── Reload recovery ───
  //
  // Waits for the boot gate, runs once, and only puts a screen up — never
  // sends anything. The next command always comes from the player.
  useEffect(() => {
    if (freeSpinsRecoveryDoneRef.current) return;
    if (!showGameAfterTransition) {
      fsDiag("scene.reload waiting for showGameAfterTransition", {
        freeSpinsReloadPhase,
      });
      return;
    }
    if (freeSpinsReloadPhase === "none") return;

    fsDiag("scene.reload branch", {
      phase: freeSpinsReloadPhase,
      active: store.getState().freeSpins.active,
      awarded: store.getState().freeSpins.awarded,
      played: store.getState().freeSpins.played,
      totalWin: store.getState().freeSpins.totalWin,
      roundWin: store.getState().freeSpins.currentRound?.win ?? null,
    });

    // ── MID-ROUND: pick the round back up, no entry sequence ──
    //
    // freeSpinsSlice has already hydrated the round from the init (awarded,
    // played, running total, room, collector, last screen) and set `active`,
    // so the bonus board is on screen from the first frame with no wipe. None
    // of it comes from localStorage: the API sends `outcome.screen` once spins
    // have been played.
    //
    // What is left: hold the UI and put the "FREE SPINS / n / LEFT" popup up.
    // Its close hands back criticalAnimationPlaying and userInterfaceDisabled,
    // and the loop is gated on the first, so the round resumes only after the
    // player dismisses it.
    if (freeSpinsReloadPhase === "in_progress") {
      freeSpinsRecoveryDoneRef.current = true;

      // Route spins to the featureSpin* flags. The transition midpoint does
      // this when entering through the wipe; there is no wipe here, so without
      // it the first free spin would drive the base game's isSpinning /
      // spinComplete and the bonus loop would never see its own.
      dispatch(setFeatureSpinActive(true));

      // ── Hold & Win (won inside free spins) on screen: only ITS popup ──
      // The bonus's own reload popup (n RESPINS LEFT) owns the screen; when it
      // hands back, the free spins carry on by themselves (freeSpinsSlice: not
      // parked, no popup owed).
      if (store.getState().holdAndWin?.active) {
        fsDiag("scene.reload — H&W on screen, no free-spins popup");
        return;
      }

      dispatch(setCriticalAnimationPlaying(true));
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setShowFreeSpinsReload(true));
      return;
    }

    // ── COMPLETION: the round closed, the total win was never shown ──
    //
    // The slice hydrated the room as active with no spins left. The scene
    // opens the outro once the screen is free (openOutroWhenReady below) and
    // leaves the UI unlocked; closing the outro starts the wipe back, as after
    // a live last spin.
    //
    // This branch also stops an unknown phase from falling through to the
    // entry replay below and locking the screen.
    if (freeSpinsReloadPhase === "completion") {
      freeSpinsRecoveryDoneRef.current = true;

      // ── The scene owns the outro on a reload ──
      // The loop's out-of-spins branch runs only once all its gates settle,
      // which a boot does not guarantee; a no-win last round would be left in
      // the room with no dialog and no way out.
      const restoredRound = store.getState().freeSpins.currentRound;
      const restoredWin = restoredRound?.win || 0;
      // The reveal runs on a losing board too. `mystery_feature` is what the
      // reveal reads, so its presence means "this board has something to open".
      const hasReveal = !!restoredRound?.storage?.mystery_feature;
      // Latched once the reveal takes the screen, so the poll cannot read a
      // clear board in the gap before the reveal raises its flag.
      let sawReveal = false;
      let revealWaits = 0;
      fsDiag("scene.reload COMPLETION", {
        restoredWin,
        hasReveal,
        // The scene opens the outro either way; a win replay only delays it.
        waitsForWinReplay: restoredWin > 0,
      });

      // ── Wait for the intro, the reveal and the win, then open ──
      //
      // Polls on a TickerTimeout until the screen is free. The player must see
      // the room, then the win, then the total; dispatched straight away the
      // dialog would land on top of the intro.
      //
      // A winning last round replays through the normal presentation. The win
      // tests are the two the live loop uses to pace its own outro, so a
      // restored round and a played one hand over at the same moment: a big
      // win holds winAnimationsPlaying until dismissed; an ordinary win holds
      // counterAnimationFinished until its display window ends, with the line
      // cycling continuing behind the dialog.
      //
      // This is the ONLY opener in the completion phase: the loop defers to it
      // (see the reloadPhase check in FreeSpinBasicControlContext).
      const openOutroWhenReady = () => {
        const s = store.getState();
        // The intro, and the free-rounds message a campaign reload opens on —
        // the room's reveal waits for that one too, so the outro must not open
        // under it, or ahead of a reveal that has not been allowed to start.
        if (selectIntroPopupActive(s) || s.userInterface?.infoPopupActive) {
          new TickerTimeout(openOutroWhenReady, 100);
          return;
        }
        // ── A Hold & Win won on the pack's last free spin goes first ──
        // (2026-10-19) restored settled beside this completion: its collection,
        // its Total Win and the wipe back to this room, then the free spins'
        // ending — the order a live round plays.
        if (s.holdAndWin?.active || s.animations?.showTransitionDialog) {
          new TickerTimeout(openOutroWhenReady, 100);
          return;
        }
        const anim = s.animations;

        // ── The mystery reveal and the Golden Mystery hold it ──
        // Neither raises the win flags. criticalAnimationPlaying is the live
        // loop's own third gate, so the scene and the loop agree on when a
        // round is still presenting.
        if (anim.criticalAnimationPlaying) {
          sawReveal = true;
          new TickerTimeout(openOutroWhenReady, 100);
          return;
        }

        // ── A reveal that already finished counts as seen ──
        // The room's win presentation starts only after the reveal, and on a
        // reload the counter starts from false, so this round's counter having
        // started proves the reveal is done. Without this, a reveal that ended
        // before the first poll would make the outro wait out the full bound.
        if (anim.counterAnimationStarted) sawReveal = true;

        // Not before it has STARTED, when the board has one to run. Bounded:
        // a reveal that never arrives must not leave the player in the room
        // with no dialog and no way out, which is the stuck session this whole
        // branch exists to prevent.
        if (hasReveal && !sawReveal && revealWaits < REVEAL_START_MAX_WAITS) {
          revealWaits += 1;
          new TickerTimeout(openOutroWhenReady, 100);
          return;
        }

        // `counterAnimationStarted` as well as finished, for the same race:
        // the counter for THIS round has not begun in the frames right after
        // the reveal ends, so "finished" is still true from the spin before it.
        if (
          restoredWin > 0 &&
          (anim.winAnimationsPlaying ||
            !anim.counterAnimationStarted ||
            !anim.counterAnimationFinished)
        ) {
          new TickerTimeout(openOutroWhenReady, 100);
          return;
        }
        fsDiag("scene.reload COMPLETION screen free -> outro", {
          restoredWin,
          sawReveal,
        });
        // A recovered round closes like a live one, MAX WIN and BETTER LUCK
        // NEXT TIME included: both are recomputed from the response
        // (winLimitReached, totalWin against the stake). Same selector as the
        // live loop in FreeSpinBasicControlContext, and the same 800ms beat as
        // its OUTRO_DELAY.
        const outroPopup = selectFreeSpinOutroPopup(store.getState());
        const outroAction =
          outroPopup === FS_OUTRO_POPUP.MAX
            ? setShowFreeSpinMaxWin
            : outroPopup === FS_OUTRO_POPUP.ZERO
              ? setShowFreeSpinZeroWin
              : setShowFreeSpinTotalWin;
        new TickerTimeout(() => dispatch(outroAction(true)), 800);
      };
      new TickerTimeout(openOutroWhenReady, 100);
      // Explicitly UNLOCKED. Other reload branches raise these and hand them
      // to the screen that takes over; here there is none, and the round
      // loop's third gate is criticalAnimationPlaying, so anything left over
      // from the boot would park the round for good.
      dispatch(setCriticalAnimationPlaying(false));
      dispatch(setUserInterfaceDisabled(false));
      return;
    }

    if (freeSpinsReloadPhase !== "intro") return;

    freeSpinsRecoveryDoneRef.current = true;

    // Held until whichever screen takes over hands back — TransitionDialog at
    // its midpoint, or the gamble dialog if the package is lost.
    dispatch(setCriticalAnimationPlaying(true));
    dispatch(setUserInterfaceDisabled(true));

    // ── INTRO: replay the whole entry sequence, not just its last screen ──
    //
    //   base game -> symbol sequence -> [gamble screen] -> intro -> transition
    //
    // The trigger round is already restored (by the provider from
    // `outcome.screen` while the state is gamble_freespins, and from the saved
    // base round afterwards), so its win presents and useWinController's
    // openFeatureScreen opens the right screen off it: the gamble screen while
    // the ladder is unanswered and has outcomes left, else the intro (an
    // emptied `variants` means the player already answered). Nothing to
    // dispatch here; a second opener would put an intro behind a live gamble.
    if (store.getState().game.currentRound?.triggerWin) return;

    // Fallback, no trigger round: nothing would present and the player would
    // be stuck in the base game holding a package, so open the screen
    // directly, by the same rule.
    console.warn(
      "[FreeSpins] Reload: no trigger round to replay, opening the feature " +
        "screen directly. Phase:",
      freeSpinsReloadPhase,
    );
    const fs = store.getState().freeSpins;
    const canGamble =
      !fs.gambleSettled && (fs.gamble?.variants?.length ?? 0) > 0;
    dispatch(canGamble ? setShowGambleDialog(true) : setShowFreeSpinIntro(true));
  }, [showGameAfterTransition, freeSpinsReloadPhase, dispatch]);

  // ─── Controller ───
  useEffect(() => {
    if (!containerRef.current || controllerRef.current) return;

    // The size as it is NOW — the shell writes it on every render.
    const {
      dimensions,
      symbolWidth,
      sceneCellHeight,
      sceneScale,
      device,
      orientation,
      layout,
      isMobileSmall,
    } = sizeRef.current;
    const realDevice = isMobileSmall ? PixiSettings.DEVICE.MOBILE_SMALL : device;

    controllerRef.current = new GameViewSimpleController(
      containerRef.current,
      dimensions,
      symbolWidth,
      sceneCellHeight,
      sceneScale,
      realDevice,
      orientation,
      layout,
      slotRegistry,
    );
    // The shell's resize effect ran before the controller existed, so the
    // first resize happens here.
    controllerRef.current.resize(
      dimensions,
      symbolWidth,
      sceneCellHeight,
      sceneScale,
      realDevice,
      orientation,
      layout,
    );

    // Shared so other components can reach the scene.
    if (gameViewControllerRef) {
      gameViewControllerRef.current = controllerRef.current;
    }

    return () => {
      if (controllerRef.current) {
        controllerRef.current.destroy();
        controllerRef.current = null;
        if (gameViewControllerRef) gameViewControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Hidden room: its spines sleep (see spineDormancy) ───
  // Same flags as the alpha below; they flip at the wipe's midpoint.
  const roomHidden = freeSpinsActive || holdAndWinActive;
  useEffect(() => {
    controllerRef.current?.setDormant(roomHidden);
  }, [roomHidden, controllerRef]);

  // ─── Golden Mystery trail animator ───
  // LevelChargingAnimator (the free-spin level boxes' trail), fired from one
  // grid cell to another: the multiplier's cell and the coin's, read off the
  // overlay containers. No spine and no live-dimensions getter, because
  // nothing is read from bones; the shell pushes resizes.
  useEffect(() => {
    if (!goldenTrailContainerRef.current) return;

    const { symbolWidth, symbolHeight } = sizeRef.current;
    const animator = new LevelChargingAnimator(
      goldenTrailContainerRef.current,
      null,
      symbolWidth,
      symbolHeight,
    );
    if (goldenTrailAnimatorRef) goldenTrailAnimatorRef.current = animator;

    return () => {
      animator.destroy();
      if (goldenTrailAnimatorRef?.current === animator) {
        goldenTrailAnimatorRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Inject the reel container into the spine's reel bone ───
  useEffect(() => {
    if (!reelSlot || !reelContainerRef.current) return;
    const node = reelContainerRef.current;
    // In the slot the bone places and scales it — drop whatever the backup
    // (below) gave it while there was no slot.
    node.position.set(REELSET_X_OFFSET, REELSET_Y_OFFSET);
    node.scale.set(1);
    reelSlot.addChild(node);
    const host = containerRef.current;
    return () => {
      if (node.parent !== reelSlot) return;
      reelSlot.removeChild(node);
      // Slot gone, game view still here: back home, where the backup can
      // place it. (On unmount @pixi/react has destroyed them already.)
      if (!node.destroyed && host && !host.destroyed) host.addChild(node);
    };
  }, [reelSlot, containerRef, reelContainerRef]);

  // ─── Inject the overlay container into the spine's overlay slot ───
  useEffect(() => {
    if (!overlaySlot || !overlayContainerRef.current) return;
    const node = overlayContainerRef.current;
    node.position.set(REELSET_X_OFFSET, REELSET_Y_OFFSET);
    // Native, like the reels: it keeps the bone's scale.
    node.scale.set(1);
    overlaySlot.addChild(node);
    const host = containerRef.current;
    return () => {
      if (node.parent !== overlaySlot) return;
      overlaySlot.removeChild(node);
      if (!node.destroyed && host && !host.destroyed) host.addChild(node);
    };
  }, [overlaySlot, containerRef, overlayContainerRef]);

  return (
    <Container
      ref={containerRef}
      name="game_view"
      sortableChildren
      zIndex={0}
      // The H&W preview (dev) hides it too, as a real Hold & Win does.
      alpha={freeSpinsActive || holdAndWinActive || FEATURES_CONFIG.DEV_TOOLS.PREVIEW_HOLD_AND_WIN_VIEW ? 0.001 : 1}
    >
      {/* Reels — injected into the spine's reel bone, drawn under the frame */}
      <Container
        ref={reelContainerRef}
        name="reel_container"
        x={REELSET_X_OFFSET}
        y={REELSET_Y_OFFSET}
        sortableChildren
        eventMode="passive"
      >
        <ReelSet
          symbolWidth={reelSetSize.symbolWidth}
          symbolHeight={reelSetSize.symbolHeight}
          dividerDimensions={reelSetSize.dividerDimensions}
          reelSpacing={reelSetSize.reelSpacing}
          // The on-screen cell height (density buckets) — only for the
          // composites' density.
          screenSymbolHeight={compositeCellHeight}
        />

        {/* Dims the board while winning symbols are highlighted. Same
            container as the reel set, so native units. Oversized on purpose:
            game_view's `reels_window_clip` clips this container to the frame
            window, so the dim covers the window edge to edge. */}
        <DimReelSet
          ref={dimReelSetRef}
          symbolWidth={reelSetSize.symbolWidth}
          symbolHeight={reelSetSize.symbolHeight}
          dividerDimensions={reelSetSize.dividerDimensions}
          paddingMultiplier={0.1}
        />

        {/* Win lines (2026-10-13): above the static reels (1) and the dim
            (2), under the overlay's winning symbols and win boxes. Oversized
            past the pillars — this container's window clip hides the ends. */}
        {FEATURES_CONFIG.WIN_LINES.ENABLED && (
          <WinLinesContainer
            ref={winLinesContainerRef}
            symbolWidth={reelSetSize.symbolWidth}
            symbolHeight={reelSetSize.symbolHeight}
            zIndex={3}
          />
        )}
      </Container>

      {/* Overlay — injected into the spine's overlay slot, drawn above the frame */}
      <Container
        ref={overlayContainerRef}
        name="overlay_reel_container"
        x={REELSET_X_OFFSET}
        y={REELSET_Y_OFFSET}
        zIndex={2}
        sortableChildren
        eventMode="passive"
      >
        <GameViewOverlayContent />
      </Container>

      {/* Golden Mystery trail layer: the multiplier's bolts to the coins it
          multiplies, and the collector's to the ones it takes.

          Its own layer, not inside either cell: FXManager emits particles
          into the layer itself, so a layer under the board would hide the
          trail. zIndex 2 beats the scene spine (1, which hosts the reels and
          the overlay) and the tap surface (0), like overlay_reel_container.

          Inside game_view, so it never draws over a dialog or popup (all
          mounted outside this scene) and it shares the scene's hide alpha. */}
      <Container
        ref={goldenTrailContainerRef}
        name="golden_trail_container"
        zIndex={2}
        sortableChildren
        eventMode="passive"
      />

      {/* Invisible full-screen tap surface for fullscreen */}
      {FEATURES_CONFIG.BACKGROUND.FULLSCREEN_CLICK_SPRITE && (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
        <BackgroundTapSurface />
        </>
      )}
    </Container>
  );
});

GameViewScene.displayName = "GameViewScene";

/**
 * GameViewSimple: the shell. Reads the size and applies it imperatively (see
 * RESIZE above); renders the memoized scene, which a resize leaves alone.
 */
const GameViewSimple = forwardRef((props, ref) => {
  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const reelContainerRef = useRef(null);
  const overlayContainerRef = useRef(null);
  const { goldenTrailAnimatorRef } = useReelRefs();

  const size = useDimensions();
  const {
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    symbolScaleExact,
    device,
    orientation,
    // The resolved layout key — finer than device + orientation, because it
    // splits desktop into small / standard / wide / ultra-wide. It is what
    // GAME_VIEW_OFFSET_CONFIG is keyed by.
    layout,
    isMobileSmall,
  } = size;
  // The spine and its native board are scaled as a whole, so they take the
  // UNROUNDED scale: continuous while the window is dragged. (The rounded
  // symbolScale steps by ~0.5% with every pixel of symbolHeight.)
  const sceneScale = symbolScaleExact ?? symbolScale;
  const sceneCellHeight = Settings.SYMBOL_HEIGHT * sceneScale;
  // The scene reads the size once, at mount (controller, golden trail).
  const sizeRef = useRef(null);
  sizeRef.current = { ...size, sceneScale, sceneCellHeight };

  const reelSlot = useSlot("gameView.reelSet");
  const overlaySlot = useSlot("gameView.overlayReelSet");

  // The composites' density only (see ReelSet): the on-screen cell, snapped
  // to the density buckets (1, 1.5, 2) — a number that changes a handful of
  // times, so the scene re-renders a handful of times, not per resize.
  const compositeCellHeight =
    Math.min(2, Math.max(1, Math.round((symbolHeight / Settings.SYMBOL_HEIGHT) * 2) / 2)) *
    Settings.SYMBOL_HEIGHT;

  // ─── The spine: scale, position, skin, reel block ───
  useLayoutEffect(() => {
    if (!controllerRef.current) return;
    const realDevice = isMobileSmall ? PixiSettings.DEVICE.MOBILE_SMALL : device;
    controllerRef.current.resize(
      dimensions,
      symbolWidth,
      sceneCellHeight,
      sceneScale,
      realDevice,
      orientation,
      layout,
    );
  }, [
    dimensions,
    symbolWidth,
    sceneCellHeight,
    sceneScale,
    device,
    orientation,
    layout,
    isMobileSmall,
  ]);

  // ─── BACKUP: no spine slot to inject into ───
  //
  // Without their slots the reel and overlay containers are plain children of
  // game_view (the spine's parent), so they get the transform the spine would
  // have given them: its position and scale, times the reel-block scale. Both
  // are native, so that is the whole resize. No frame or window clip without
  // the spine; ReelSetMask still clips the reels.
  useLayoutEffect(() => {
    if (reelSlot && overlaySlot) return;
    const board = resolveBoardPlacement({
      dimensions,
      symbolHeight: sceneCellHeight,
      symbolScale: sceneScale,
      layout,
    });
    const reels = reelContainerRef.current;
    if (reels && !reelSlot) {
      reels.position.set(board.x, board.y);
      reels.scale.set(board.scale * board.reelScale);
    }
    const overlay = overlayContainerRef.current;
    if (overlay && !overlaySlot) {
      overlay.position.set(board.x, board.y);
      // Native, like the reels.
      overlay.scale.set(board.scale * board.reelScale);
    }
  }, [reelSlot, overlaySlot, dimensions, sceneCellHeight, sceneScale, layout]);

  // ─── Golden Mystery trail ─── the particle scale and the flight distance
  // are both measured in cells.
  useLayoutEffect(() => {
    goldenTrailAnimatorRef?.current?.resize(symbolWidth, symbolHeight);
  }, [goldenTrailAnimatorRef, symbolWidth, symbolHeight]);

  return (
    <GameViewScene
      sizeRef={sizeRef}
      controllerRef={controllerRef}
      containerRef={containerRef}
      reelContainerRef={reelContainerRef}
      overlayContainerRef={overlayContainerRef}
      reelSlot={reelSlot}
      overlaySlot={overlaySlot}
      compositeCellHeight={compositeCellHeight}
    />
  );
});

GameViewSimple.displayName = "GameViewSimple";

export default GameViewSimple;
